import type { PlatformComponentProps } from "@b2bportal/core-types";
import {
  useScrolltopOnPathChange,
  ErrorBoundary,
  TRIPS_ERROR_PATH,
} from "@b2bportal/core-utilities";
import clsx from "clsx";
import "./App.scss";
import {
  TRIPS_HTS_EVENT_TRANSFORMER_HOOKS,
  type TripsHTSEvent,
  type TripsRawEvent,
} from "./tracking";
import { useMemo } from "react";
import {
  type AEvent,
  createUseEmitRawEvent,
  type EventTransformerHooks,
  type ExtractSourceEvent,
  type PublishEvent,
  TrackingProvider,
  usePublishHTSEvent,
} from "@b2bportal/core-tracking";

type TransformerHooks = EventTransformerHooks<TripsRawEvent, AEvent>;

interface TripsAppComponentProps extends PlatformComponentProps {
  publishHTSEvent?: PublishEvent<TripsHTSEvent>;
  transformerHooks?: TransformerHooks;
}

export const TripsApp = ({
  children,
  className,
  publishHTSEvent,
  transformerHooks,
}: TripsAppComponentProps) => {
  useScrolltopOnPathChange();

  const defaultPublishHTSEvent = usePublishHTSEvent();

  const emitRawEventHooks = useMemo(
    () => [
      createUseEmitRawEvent<
        TripsRawEvent,
        AEvent,
        TransformerHooks,
        ExtractSourceEvent<TransformerHooks>,
        ExtractSourceEvent<TransformerHooks>
      >({
        transformerHooks: transformerHooks ?? TRIPS_HTS_EVENT_TRANSFORMER_HOOKS,
        usePublishEvent: () => publishHTSEvent ?? defaultPublishHTSEvent,
      }),
    ],
    [publishHTSEvent, defaultPublishHTSEvent, transformerHooks]
  );

  return (
    <TrackingProvider emitRawEventHooks={emitRawEventHooks}>
      <ErrorBoundary errorPath={TRIPS_ERROR_PATH}>
        <div className={clsx("core-trip-app", className)}>{children}</div>
      </ErrorBoundary>
    </TrackingProvider>
  );
};

export default TripsApp;
